import { type ChangeEventHandler, type ReactNode, Fragment, useCallback, useId } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import ListItemPrice from 'LuxKit/List/ListItemPrice'
import RadioButton from 'LuxKit/Radio/RadioButton'
import Divider from 'LuxKit/Divider'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import srOnly from 'styles/tools/sr-only'
import focusStyle from 'styles/utils/focusStyle'

const QuantityOption = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${rem(8)} ${rem(16)};
  gap: ${rem(12)};
  border-radius: 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;
  &:has(input:disabled) {
    cursor: default;
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  input {
    ${srOnly}
  }

  &:has(input:focus-visible) {
    ${focusStyle}
    outline-offset: -2px;
  }

  &:has(input:checked) {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
  }

  ${mediaHoverable} {
    &&:not(:has(input:disabled)):hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
    }
  }
`

const Price = styled.div`
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: ${props => props.theme.font.primary.weight.superBold};
  * { font-weight: inherit; }
`

export interface AccommodationRoomQuantityOption {
  quantity: number
  /** An already resolved total for this quantity; no price arithmetic belongs in this control. */
  price?: number
  /** Optional feature-owned display for a resolved price with membership or other presentation rules. */
  priceContent?: ReactNode
  disabled?: boolean
}

interface Props {
  value: number
  options: ReadonlyArray<AccommodationRoomQuantityOption>
  label: string
  onChange: (quantity: number) => void
  presentation?: 'dropdown' | 'sheet'
}

function AccommodationRoomQuantityOptions({ value, options, label, onChange, presentation = 'dropdown' }: Props) {
  const intl = useIntl()
  const name = useId()
  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    const quantity = Number(event.currentTarget.value)
    if (options.some(option => option.quantity === quantity && !option.disabled)) {
      onChange(quantity)
    }
  }, [onChange, options])

  return <div role="radiogroup" aria-label={label}>
    {options.map((option, index) => <Fragment key={option.quantity}>
      <QuantityOption>
        <RadioButton
          name={name}
          value={option.quantity}
          checked={value === option.quantity}
          disabled={option.disabled}
          onChange={handleChange}
          aria-label={intl.formatMessage({
            id: '_n5ZFcXchhIPcWY_zgwv',
            defaultMessage: '{count, plural, one {# room} other {# rooms}}',
            description: 'Quantity choice describing how many rooms will be selected for an accommodation package',
          }, { count: option.quantity })}
        />
        <BodyText variant="medium">{String(option.quantity)}</BodyText>
        {option.quantity > 0 && <Price>
          {option.priceContent ?? <ListItemPrice price={option.price} size="medium" />}
        </Price>}
      </QuantityOption>
      {presentation === 'sheet' && index < options.length - 1 && <Divider kind="primary" />}
    </Fragment>)}
  </div>
}

export default AccommodationRoomQuantityOptions
